/**
 * Google Analytics 4 (GA4) Utility Functions
 * Handles page views and event tracking
 */

// GA4 Measurement ID
export const GA_MEASUREMENT_ID = 'G-J4XTHN1DST';

// GDPR gate: for an EEA/UK visitor who hasn't consented, don't call gtag at all.
// (Google Consent Mode v2 in index.html already withholds GA cookies for those
// regions; this stops the calls too.) Non-EEA/UK visitors are never blocked.
import { trackingAllowed } from '@/utils/consent';

/**
 * Send page view to Google Analytics
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!trackingAllowed()) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title,
    });
    
    console.log('📊 GA4 Page View:', { path, title });
  }
};

/**
 * Track custom events in Google Analytics
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!trackingAllowed()) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);

    console.log('📊 GA4 Event:', eventName, eventParams);
  }
};

/**
 * Track user sign up
 * @param {string} method - Sign up method (email, google, etc.)
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track user login
 * @param {string} method - Login method (email, google, etc.)
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track resume upload
 * @param {string} filename - Name of the uploaded file
 */
export const trackResumeUpload = (filename) => {
  trackEvent('resume_upload', {
    file_name: filename,
  });
};

/**
 * Track resume analysis completion
 * @param {number} score - ATS score received
 * @param {string} targetRole - Target role selected
 */
export const trackResumeAnalysis = (score, targetRole) => {
  trackEvent('resume_analysis_complete', {
    ats_score: score,
    target_role: targetRole,
  });
};

/**
 * Track payment initiation (begin_checkout)
 * @param {string} product - Product name
 * @param {number} value - Price value
 * @param {string} currency - Currency code
 */
export const trackBeginCheckout = (product, value, currency = 'INR') => {
  trackEvent('begin_checkout', {
    currency: currency,
    value: value,
    items: [{
      item_name: product,
      price: value,
    }],
  });
};

/**
 * Track successful purchase
 * @param {string} transactionId - Transaction ID
 * @param {number} value - Purchase value
 * @param {string} product - Product name
 * @param {string} currency - Currency code
 */
export const trackPurchase = (transactionId, value, product, currency = 'INR') => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: [{
      item_name: product,
      price: value,
    }],
  });
};

/**
 * Track job application
 * @param {string} jobTitle - Job title
 * @param {string} company - Company name
 */
export const trackJobApplication = (jobTitle, company) => {
  trackEvent('job_application', {
    job_title: jobTitle,
    company: company,
  });
};

/**
 * Track skill path generation
 * @param {string} targetRole - Target role
 * @param {number} coursesCount - Number of courses suggested
 */
export const trackSkillPathGeneration = (targetRole, coursesCount) => {
  trackEvent('skill_path_generated', {
    target_role: targetRole,
    courses_count: coursesCount,
  });
};

/**
 * Track AI companion chat
 * @param {string} skill - Skill selected (resume_writing, interview_prep, etc.)
 */
export const trackCompanionChat = (skill) => {
  trackEvent('companion_chat', {
    skill: skill,
  });
};

/**
 * Track resume template download
 * @param {string} format - Download format (pdf, docx)
 */
export const trackResumeDownload = (format) => {
  trackEvent('resume_download', {
    format: format,
  });
};

/**
 * Track contact form submission
 */
export const trackContactSubmission = () => {
  trackEvent('contact_form_submit');
};

/**
 * Track CTA button clicks
 * @param {string} buttonName - Name/location of the CTA
 * @param {string} destination - Where the CTA leads
 */
export const trackCTAClick = (buttonName, destination) => {
  trackEvent('cta_click', {
    button_name: buttonName,
    destination: destination,
  });
};

/**
 * Track search queries
 * @param {string} searchTerm - The search term
 * @param {string} searchType - Type of search (jobs, courses, etc.)
 */
export const trackSearch = (searchTerm, searchType) => {
  trackEvent('search', {
    search_term: searchTerm,
    search_type: searchType,
  });
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 */
export const trackError = (errorType, errorMessage) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (!trackingAllowed()) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

/**
 * Set user ID for cross-device tracking
 * @param {string} userId - User ID
 */
export const setUserId = (userId) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId,
    });
  }
};
